import { createElement, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import type { SignatureDocument } from '@worki/shared';
import { api } from '../../lib/api.js';
import { sendActionOf } from '../../lib/sendAction.js';
import { SendDocumentCopyDialog } from '../../components/SendQuoteDialog.js';
import { useToast } from '../../components/Toast.js';

/**
 * Chasing and deleting a document, from wherever it is listed.
 *
 * The signing screen and the send history both show the same rows, and both
 * offer the same two actions on them. Written twice, they drift: one of them
 * ends up asking which channel to use while the other doesn't, and the answer
 * to "what does 'send again' do" starts depending on which list you found the
 * document in.
 */
export function useDocumentActions(onChanged: () => void) {
  const { t } = useTranslation();
  const toast = useToast();
  // The signed document whose copy is being filled in, while its popup is open.
  const [copying, setCopying] = useState<SignatureDocument | null>(null);

  const resend = { mutate: (document: SignatureDocument) => setCopying(document) };

  /** The popup, for the screen to draw wherever it likes: it is a dialog, so not in any one place. */
  const copyDialog: ReactNode = copying
    ? createElement(SendDocumentCopyDialog, {
        open: true,
        document: copying,
        copy: sendActionOf('document',copying) === 'copy',
        onClose: () => setCopying(null),
        onSent: onChanged,
      })
    : null;

  const remove = useMutation({
    mutationFn: (document: SignatureDocument) => api.del(`/signatures/${document.id}`),
    onSuccess: () => {
      onChanged();
      toast.success(t('common.deleted'));
    },
    onError: () => toast.error(t('errors.unknown')),
  });

  return { resend, remove, copyDialog };
}
